/**
 * «Обсуждения» — Spec 022. Every thread — the shared chat, the discussions of
 * tasks and ideas — by the latest activity, with its last line, as in Telegram.
 * Stage 2 (task a60d4d9a): a line with something new for me carries its counter,
 * from the read marks the server keeps per person and discussion — a phone and a
 * computer count the same. On a computer the thread opens on the right.
 */

import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Briefcase, CheckSquare, Lightbulb, MessageSquare, StickyNote } from "lucide-react";

import { Chat, GENERAL_CHAT, WORKSPACE_CHAT_ID, type ChatMessage } from "@/api/chat";
import { ChatThread } from "@/components/ChatThread";
import { Button } from "@/components/ui/Button";
import { SkeletonList } from "@/components/ui/Skeleton";
import { AppBar, ago, EmptyNote, ListPane, ScreenHeader, useIsDesktop, whoDid } from "@/kit";
import { plainText } from "@/lib/markdown";
import { useBackButton } from "@/lib/telegram-ui";
import { cn } from "@/lib/cn";
import { countLabel } from "@/lib/unread";
import { TaskScreen } from "@/screens/TaskScreen";

interface Thread {
  key: string;
  kind: "chat" | "task" | "idea" | "note" | "case";
  id: string | null;
  title: string;
  /** None only for the shared chat before its first message. */
  last?: ChatMessage;
}

const OWN: string[] = ["task", "idea", "note", "case"];

/** The latest message of each thread, newest thread first; a case talks in its own thread (Spec 025). */
export function threadsOf(messages: ChatMessage[]): Thread[] {
  const byKey = new Map<string, Thread>();
  for (const m of messages) {
    const kind = (OWN.includes(m.parent_type) ? m.parent_type : "chat") as Thread["kind"];
    const key = kind === "chat" ? "chat" : `${kind}:${m.parent_id}`;
    const seen = byKey.get(key);
    if (!seen || seen.last!.created_at < m.created_at) {
      byKey.set(key, { key, kind, id: kind === "chat" ? null : m.parent_id, title: kind === "chat" ? "Общий чат" : m.context_title, last: m });
    }
  }
  return [...byKey.values()].sort((a, b) => b.last!.created_at.localeCompare(a.last!.created_at));
}

const ICON = { chat: MessageSquare, task: CheckSquare, idea: Lightbulb, note: StickyNote, case: Briefcase } as const;
const TINT = { chat: "bg-p-review-tint text-p-review", task: "bg-p-surface-2 text-p-text", idea: "bg-p-now-tint text-p-text", note: "bg-p-surface-2 text-p-text", case: "bg-p-surface-2 text-p-text" } as const;

/** Where a discussion opens: a task and a case on their discussion block. */
export function hrefOf(t: Pick<Thread, "kind" | "id">): string {
  if (t.kind === "chat") return "/chat/general";
  if (t.kind === "task") return `/tasks/${t.id}?block=discussion`;
  if (t.kind === "case") return `/knowledge/cases/${t.id}?block=discussion`;
  return `/knowledge/sources/${t.kind}/${t.id}`;
}

/** The server counts by discussion: «task:<id>», the shared chat by its id too. */
export function unreadOf(unread: Record<string, number> | undefined, t: Pick<Thread, "kind" | "id">): number {
  return unread?.[t.kind === "chat" ? `chat:${WORKSPACE_CHAT_ID}` : `${t.kind}:${t.id}`] ?? 0;
}

/** The counter of a line — the bottom bar's badge, in the line's corner. */
export function UnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      className="h-[18px] min-w-[18px] rounded-full bg-p-needs px-[5px] text-center text-[11px] font-bold leading-[18px] text-p-surface"
      aria-label={`новых: ${count}`}
    >
      {countLabel(count)}
    </span>
  );
}

function preview(m: ChatMessage): string {
  // PC audit 28.09, point 6: the markers go, not every «_»; an agent's line is its agent's.
  const text = plainText(m.summary_md || m.body_md) || (m.attachments.length ? "Вложение" : "");
  return `${whoDid(m.author_name, Boolean(m.via_token_id))}: ${text}`;
}

export function ThreadsScreen() {
  const desktop = useIsDesktop();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // PC audit 28.09, point 7: the server lists every discussion, not the last hundred
  // messages, so a quiet one no longer falls off; older ones come with «Показать ещё».
  const feed = useInfiniteQuery({
    queryKey: ["chat", "threads"],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => Chat.threads(pageParam),
    getNextPageParam: (page) => page.next_before ?? undefined,
    refetchInterval: 15_000,
  });
  const pages = feed.data?.pages ?? [];
  // threadsOf keeps one line per discussion even if a refetch moved it between pages.
  const threads = threadsOf(pages.flatMap((p) => (p.general ? [p.general, ...p.threads] : p.threads)));
  // Every page carries the whole workspace's counters; the first one is refetched first.
  const unread = pages[0]?.unread;
  if (!threads.some((t) => t.kind === "chat")) threads.push({ key: "chat", kind: "chat", id: null, title: "Общий чат" });
  // The general chat stays on top, where people look for it first (point 18).
  threads.sort((a, b) => Number(b.kind === "chat") - Number(a.kind === "chat"));
  const open = params.get("open");

  const select = (t: Thread) => {
    if (desktop && (t.kind === "chat" || t.kind === "task")) {
      const next = new URLSearchParams(params);
      next.set("open", t.kind === "chat" ? "chat" : t.id!);
      setParams(next, { replace: true });
    } else navigate(hrefOf(t));
  };

  const list = (
    <div className="flex flex-col gap-3">
      <ScreenHeader title="Обсуждения" />
      {feed.isLoading && <SkeletonList count={5} />}
      {feed.isError && <EmptyNote>Не удалось загрузить обсуждения. Проверь связь.</EmptyNote>}
      <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
        {threads.map((t) => {
          const Icon = ICON[t.kind];
          const selected = open === (t.kind === "chat" ? "chat" : t.id);
          const count = unreadOf(unread, t);
          return (
            <li key={t.key} className="border-b border-p-border last:border-0">
              <button type="button" onClick={() => select(t)}
                className={cn("flex w-full items-start gap-3 px-4 py-3 text-left press-surface", selected && "bg-p-surface-2")}>
                <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-p-border", TINT[t.kind])}>
                  <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  {/* Two lines: a task and an idea with the same start stay apart (point 18). */}
                  <span className="block text-[15px] font-semibold leading-5 text-p-text [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden">{t.title}</span>
                  <span className={cn("block truncate text-body", count > 0 ? "text-p-text" : "text-p-text-muted")}>{t.last ? preview(t.last) : "Начни общий разговор"}</span>
                </span>
                {(t.last || count > 0) && (
                  <span className="flex shrink-0 flex-col items-end gap-1">
                    {t.last && <span className="text-chip text-p-text-muted">{ago(t.last.created_at)}</span>}
                    <UnreadBadge count={count} />
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      {feed.hasNextPage && (
        <Button variant="ghost" block loading={feed.isFetchingNextPage} onClick={() => void feed.fetchNextPage()}>
          Показать ещё
        </Button>
      )}
      <p className="flex flex-wrap gap-x-4 gap-y-1 text-meta">
        <Link to="/chat/all" className="font-semibold text-p-link">Вся переписка одной лентой</Link>
        <Link to="/settings/notifications" className="font-semibold text-p-link">Уведомления</Link>
      </p>
    </div>
  );

  if (!desktop) return list;
  const close = () => {
    const next = new URLSearchParams(params);
    next.delete("open");
    setParams(next, { replace: true });
  };
  return (
    <ListPane
      list={list}
      open={Boolean(open)}
      onClose={close}
      pane={open === "chat" ? (
        <div className="flex flex-col gap-3">
          <ScreenHeader title="Общий чат" />
          <ChatThread context={GENERAL_CHAT} pinToLatest hideSearch stickyComposer placeholder="Написать в общий чат" />
        </div>
      ) : open ? <TaskScreen key={open} taskId={open} embedded /> : null}
      placeholder={<EmptyNote>Выбери обсуждение слева — оно откроется здесь.</EmptyNote>}
    />
  );
}

/** The shared chat on its own screen: its messages only, the field at the bottom. */
export function GeneralChatScreen() {
  useBackButton("/chat");
  return (
    <div className="flex flex-col gap-3">
      <AppBar fallback="/chat" context="Обсуждения" />
      <ScreenHeader title="Общий чат" />
      <ChatThread context={GENERAL_CHAT} pinToLatest stickyComposer placeholder="Написать в общий чат" />
    </div>
  );
}
